import { type FC, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Modal, Radio, Select, Spin, Tag } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import type { PromoLibraryKpiColumnConfig } from '../../config/promoLibraryConfig';
import type { ResetOption } from '../types';

export interface ResetKpisPayload {
    kpiKeys: string[];
    resetOption: ResetOption;
}

export interface ResetKpisModalProps {
    open: boolean;
    editableKpis: PromoLibraryKpiColumnConfig[];
    loading: boolean;
    hasActiveFilters: boolean;
    onSubmit: (payload: ResetKpisPayload) => void;
    onCancel: () => void;
}

interface TagProps {
    label: ReactNode;
    value: string;
    closable: boolean;
    onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SELECT_ALL_VALUE = '__select_all__';
const SELECT_ALL_OPTION = { label: 'Select All', value: SELECT_ALL_VALUE };
const SPIN_ICON = <LoadingOutlined style={{ fontSize: 14 }} spin />;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ResetKpisModal: FC<ResetKpisModalProps> = ({
    open,
    editableKpis,
    loading,
    hasActiveFilters,
    onSubmit,
    onCancel,
}) => {
    const [selectedKpiKeys, setSelectedKpiKeys] = useState<string[]>([]);
    const [resetOption, setResetOption] = useState<ResetOption>('filter_items');

    const allKpiKeys = useMemo(() => editableKpis.map((kpi) => kpi.key), [editableKpis]);

    const optionsWithSelectAll = useMemo(
        () =>
            editableKpis.length
                ? [SELECT_ALL_OPTION, ...editableKpis.map((kpi) => ({ label: kpi.label, value: kpi.key }))]
                : [],
        [editableKpis],
    );

    const handleKpiChange = useCallback(
        (values: string[]) => {
            const hasSelectAll = values.includes(SELECT_ALL_VALUE);
            const hadSelectAll = selectedKpiKeys.includes(SELECT_ALL_VALUE);

            if (hadSelectAll && !hasSelectAll) {
                setSelectedKpiKeys([]);
                return;
            }
            if (!hadSelectAll && hasSelectAll) {
                setSelectedKpiKeys([SELECT_ALL_VALUE, ...allKpiKeys]);
                return;
            }
            setSelectedKpiKeys(values);
        },
        [selectedKpiKeys, allKpiKeys],
    );

    const tagRender = useCallback((props: TagProps) => {
        if (props.value === SELECT_ALL_VALUE) return <></>;
        return (
            <Tag closable={props.closable} onClose={props.onClose} style={{ marginRight: 4 }}>
                {props.label}
            </Tag>
        );
    }, []);

    const actualSelectedKpis = useMemo(
        () => selectedKpiKeys.filter((k) => k !== SELECT_ALL_VALUE),
        [selectedKpiKeys],
    );

    const isApplyDisabled = actualSelectedKpis.length === 0;

    const handleOk = useCallback(() => {
        if (loading || isApplyDisabled) return;
        onSubmit({ kpiKeys: actualSelectedKpis, resetOption });
    }, [loading, isApplyDisabled, onSubmit, actualSelectedKpis, resetOption]);

    const handleCancel = useCallback(() => {
        setSelectedKpiKeys([]);
        setResetOption('filter_items');
        onCancel();
    }, [onCancel]);

    return (
        <Modal
            open={open}
            centered
            destroyOnClose
            width={420}
            title={<p className="font-sm">Reset KPIs to Default Values</p>}
            okText={loading ? <Spin indicator={SPIN_ICON} style={{ color: 'white' }} /> : 'Reset'}
            okButtonProps={{ disabled: isApplyDisabled || loading, danger: true, style: { borderRadius: 6 } }}
            cancelButtonProps={{ disabled: loading, style: { borderRadius: 6 } }}
            onOk={handleOk}
            onCancel={loading ? undefined : handleCancel}
        >
            <div className="rounded border border-[#EAEAEC] p-4">
                {/* KPI selection */}
                <div className="mb-4">
                    <label className="mb-2 block text-xs">Select KPI&apos;s to Reset</label>
                    <Select
                        mode="multiple"
                        showSearch={false}
                        placeholder="Select KPI's"
                        className="w-full"
                        value={selectedKpiKeys}
                        options={optionsWithSelectAll}
                        maxTagCount="responsive"
                        onChange={handleKpiChange}
                        tagRender={tagRender}
                    />
                </div>

                {/* Reset scope */}
                <div>
                    <label className="mb-2 block text-xs">Choose reset option</label>
                    <Radio.Group
                        value={resetOption}
                        onChange={(e) => setResetOption(e.target.value)}
                        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                        <Radio value="filter_items" disabled={!hasActiveFilters}>
                            Reset only filter items
                        </Radio>
                        <Radio value="all">Reset All</Radio>
                    </Radio.Group>
                </div>
            </div>
        </Modal>
    );
};

ResetKpisModal.displayName = 'ResetKpisModal';
export default ResetKpisModal;
